/**
 * PublicSearchBar — debounced search input for public list pages.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTheme } from '../../theme/ThemeContext';

export default function PublicSearchBar({ onSearch, onClear, placeholder = 'Search…', resetKey }) {
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);
  const timerRef = useRef(null);
  const { theme } = useTheme();
  const accentColor = theme.primary;

  // The typed text lives in this component, so a parent that drops the query
  // from the outside — the toolbar's own "Clear" button — had no way to reach
  // it: the results reset but the box kept showing a term that was no longer
  // being applied, and the next keystroke appended to it. Bumping `resetKey`
  // brings the visible text back in step.
  //
  // The pending debounce is dropped with it. Clearing within 500ms of the last
  // keystroke would otherwise let that timer fire afterwards and re-apply the
  // very query that was just cleared.
  useEffect(() => {
    if (resetKey === undefined) return;
    setValue('');
    if (timerRef.current) clearTimeout(timerRef.current);
  }, [resetKey]);

  const handleChange = useCallback((e) => {
    const v = e.target.value;
    setValue(v);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onSearch(v.trim()), 500);
  }, [onSearch]);

  const handleClear = () => {
    setValue('');
    if (timerRef.current) clearTimeout(timerRef.current);
    if (onClear) onClear();
    else onSearch('');
  };

  return (
    <div style={{ position: 'relative', minWidth: 200, maxWidth: 300, flexShrink: 0 }}>
      <i
        className="bi bi-search"
        style={{
          position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)',
          color: focused ? accentColor : '#94a3b8',
          fontSize: '0.82rem', pointerEvents: 'none',
          transition: 'color 200ms ease', zIndex: 1,
        }}
      />
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: '100%',
          paddingLeft: 36,
          paddingRight: value ? 34 : 14,
          paddingTop: 7,
          paddingBottom: 7,
          fontSize: '0.85rem',
          border: `1.5px solid ${focused ? accentColor : '#e2e8f0'}`,
          borderRadius: 'var(--button-radius)',
          background: '#fff',
          outline: 'none',
          color: '#1e293b',
          boxShadow: focused
            ? `0 0 0 3px ${accentColor}22, 0 2px 8px rgba(0,0,0,0.06)`
            : '0 1px 4px rgba(0,0,0,0.06)',
          transition: 'border-color 200ms ease, box-shadow 200ms ease',
        }}
      />
      {value && (
        <button
          type="button"
          onClick={handleClear}
          style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', padding: 2, cursor: 'pointer',
            color: '#94a3b8', fontSize: '0.95rem', lineHeight: 1,
            display: 'flex', alignItems: 'center',
            transition: 'color 150ms ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = '#64748b'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; }}
          aria-label="Clear search"
        >
          <i className="bi bi-x-circle-fill" />
        </button>
      )}
    </div>
  );
}
